import { useId, useState } from "react";
import Button from "@/components/Button";
import Icon from "@/components/Icon";
import IconButton from "@/components/IconButton";
import Radio from "@/components/Radio";
import Tag from "@/components/Tag";
import TextField from "@/components/TextField";
import {
  transcriptSnippet,
  transcriptSourceForText,
  transcriptTitleForText,
  type EmulatorTranscriptEntry
} from "./transcripts";

export type TranscriptPickerProps = {
  history: EmulatorTranscriptEntry[];
  /** Effective staged text (never blank — the caller resolves the fallback). */
  selectedText: string;
  onSelect: (text: string) => void;
  onRemove: (id: string) => void;
  onRestore: () => void;
  disabled?: boolean;
};

/**
 * Searchable single-select transcript picker. Replaces the old wall of full
 * transcript texts: each entry shows a short title plus a snippet and a
 * Sample/Recent tag, custom text lives behind "Write custom", and the full
 * selected text previews below the list for verification before delivery.
 *
 * Always sits inside a default-collapsed accordion and renders full-height:
 * the list and preview deliberately do not clip or scroll on their own, so
 * there is no scrolling window inside the accordion — the dock keeps one
 * scroll region, its own.
 */
export default function TranscriptPicker({
  history,
  selectedText,
  onSelect,
  onRemove,
  onRestore,
  disabled = false
}: TranscriptPickerProps) {
  const titleIdPrefix = useId();
  const [search, setSearch] = useState("");
  const [customMode, setCustomMode] = useState(false);
  const [customDraft, setCustomDraft] = useState("");

  const selectedId = history.find((entry) => entry.text === selectedText)?.id ?? null;
  const isCustomSelected = selectedText.trim() !== "" && selectedId === null;

  const normalizedSearch = search.trim().toLowerCase();
  const filtered = normalizedSearch
    ? history.filter((entry) => {
        const title = transcriptTitleForText(entry.text).toLowerCase();
        return title.includes(normalizedSearch) || entry.text.toLowerCase().includes(normalizedSearch);
      })
    : history;

  const enterCustomMode = () => {
    setCustomDraft(isCustomSelected ? selectedText : "");
    setCustomMode(true);
  };

  const useCustomText = () => {
    const trimmed = customDraft.trim();
    if (!trimmed) return;
    onSelect(trimmed);
    setCustomMode(false);
  };

  if (customMode) {
    return (
      <div className="flex flex-col gap-3" data-testid="transcript-picker">
        <TextField
          key="custom-transcript"
          label="Custom transcript"
          placeholder="Paste or write the transcript…"
          value={customDraft}
          onChangeText={setCustomDraft}
          supportingText="Saved to your recents when delivered."
          type="textarea"
          rows={4}
          variant="outlined"
          className="w-full"
          disabled={disabled}
        />
        <div className="flex flex-wrap gap-2">
          <Button variant="filled" onClick={useCustomText} disabled={disabled || !customDraft.trim()}>
            Use this text
          </Button>
          <Button variant="text" onClick={() => setCustomMode(false)} disabled={disabled}>
            Back to library
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="transcript-picker">
      {history.length > 0 && (
        <TextField
          key="search-transcripts"
          label="Search transcripts"
          placeholder="Search by topic or keyword…"
          value={search}
          onChangeText={setSearch}
          leadingIcon="search"
          variant="outlined"
          className="w-full"
          disabled={disabled}
        />
      )}

      {history.length === 0 ? (
        <div className="flex flex-col gap-2">
          <p className="text-body-medium text-sys-brand-on-surface-variant">Library empty.</p>
          <div className="flex flex-wrap gap-2">
            <Button variant="text" onClick={onRestore} disabled={disabled}>
              Restore samples
            </Button>
            <Button variant="text" onClick={enterCustomMode} disabled={disabled}>
              Write custom
            </Button>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col gap-2">
          <p className="text-body-medium text-sys-brand-on-surface-variant">
            No transcripts match &ldquo;{search.trim()}&rdquo;.
          </p>
          <div>
            <Button variant="text" onClick={() => setSearch("")} disabled={disabled}>
              Clear search
            </Button>
          </div>
        </div>
      ) : (
        <div role="radiogroup" aria-label="Choose a transcript" className="rounded-lg border border-sys-brand-outline">
          {filtered.map((entry, index) => {
            const title = transcriptTitleForText(entry.text);
            const source = transcriptSourceForText(entry.text);
            const checked = entry.id === selectedId;
            const titleId = `${titleIdPrefix}-transcript-title-${index}`;
            return (
              <div
                key={entry.id}
                className={`flex items-start gap-1 p-2 ${index > 0 ? "border-t border-sys-brand-outline" : ""} ${
                  checked ? "bg-sys-brand-secondary-container/40" : ""
                }`}
              >
                <div className="min-w-0 flex-1">
                  <Radio
                    label={
                      <span className="flex flex-wrap items-center gap-2">
                        <span id={titleId}>{title}</span>
                        <Tag
                          tagType={source === "sample" ? "information" : "secondary"}
                          tagStyle="standard"
                          size="small"
                        >
                          {source === "sample" ? "Sample" : "Recent"}
                        </Tag>
                      </span>
                    }
                    supportingText={transcriptSnippet(entry.text)}
                    checked={checked}
                    onChange={(next) => {
                      if (next) onSelect(entry.text);
                    }}
                    name="flex-emulator-transcript-picker"
                    id={`flex-emulator-transcript-${entry.id}`}
                    aria-labelledby={titleId}
                    disabled={disabled}
                  />
                </div>
                <IconButton
                  name="close"
                  ariaLabel="Remove saved transcript"
                  onClick={() => onRemove(entry.id)}
                  disabled={disabled}
                  skipMargin
                />
              </div>
            );
          })}
        </div>
      )}

      {history.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-body-small text-sys-brand-on-surface-variant">
            {normalizedSearch ? `${filtered.length} of ${history.length} saved` : `${history.length} saved`}
          </p>
          <Button variant="text" onClick={enterCustomMode} disabled={disabled}>
            {isCustomSelected ? "Edit custom" : "Write custom"}
          </Button>
        </div>
      )}

      {selectedText.trim() !== "" && (
        <div>
          <p className="mb-1 flex items-center gap-1 text-label-medium text-sys-brand-on-surface-variant">
            <span aria-hidden="true" className="flex items-center">
              <Icon name="check" size={16} />
            </span>
            {isCustomSelected ? "Custom transcript" : transcriptTitleForText(selectedText)}
          </p>
          <p className="rounded-lg border border-sys-brand-outline-variant bg-sys-brand-surface-container p-2 text-body-small text-sys-brand-on-surface">
            {selectedText}
          </p>
        </div>
      )}
    </div>
  );
}
